import { supabase } from '../lib/supabase';
import { logger, generateRequestId } from '../utils/logger';

/**
 * Service for privacy settings management
 */

export const privacyService = {
  /**
   * Get current user's privacy settings
   */
  async getPrivacySettings() {
    const requestId = generateRequestId();
    logger?.setRequestId(requestId);

    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) {
        return { data: null, error: { message: 'Not authenticated' } };
      }

      const { data, error } = await supabase
        ?.from('privacy_settings')
        ?.select('*')
        ?.eq('user_id', user?.id)
        ?.single();

      if (error && error?.code !== 'PGRST116') {
        logger?.error('Failed to fetch privacy settings', error, { requestId });
        return { data: null, error };
      }

      // Create default settings if none exist
      if (!data) {
        const { data: newSettings, error: createError } = await supabase
          ?.from('privacy_settings')
          ?.insert([{ user_id: user?.id }])
          ?.select()
          ?.single();

        if (createError) {
          return { data: null, error: createError };
        }

        return { data: newSettings, error: null };
      }

      return { data, error: null };
    } catch (error) {
      logger?.error('Privacy settings fetch error', error, { requestId });
      return { data: null, error: { message: 'Failed to fetch privacy settings' } };
    }
  },

  /**
   * Update privacy settings
   */
  async updatePrivacySettings(updates) {
    const requestId = generateRequestId();
    logger?.setRequestId(requestId);

    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) {
        return { data: null, error: { message: 'Not authenticated' } };
      }

      const { data, error } = await supabase
        ?.from('privacy_settings')
        ?.update(updates)
        ?.eq('user_id', user?.id)
        ?.select()
        ?.single();

      if (error) {
        logger?.error('Failed to update privacy settings', error, { requestId });
        return { data: null, error };
      }

      logger?.audit('PRIVACY_SETTINGS_UPDATED', user?.id, { fields: Object.keys(updates) });
      return { data, error: null };
    } catch (error) {
      logger?.error('Privacy settings update error', error, { requestId });
      return { data: null, error: { message: 'Failed to update privacy settings' } };
    }
  },

  /**
   * Request data deletion
   */
  async requestDataDeletion(reason) {
    const requestId = generateRequestId();
    logger?.setRequestId(requestId);

    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) {
        return { data: null, error: { message: 'Not authenticated' } };
      }

      logger?.audit('DATA_DELETION_REQUESTED', user?.id, { reason });
      
      // In production, this would trigger a data deletion workflow
      return { data: { message: 'Data deletion request submitted' }, error: null };
    } catch (error) {
      logger?.error('Data deletion request error', error, { requestId });
      return { data: null, error: { message: 'Failed to request data deletion' } };
    }
  },

  /**
   * Export user data
   */
  async exportUserData() {
    const requestId = generateRequestId();
    logger?.setRequestId(requestId);

    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) {
        return { data: null, error: { message: 'Not authenticated' } };
      }

      // Fetch all user data
      const [profile, symptoms, sessions, privacy] = await Promise.all([
        supabase?.from('user_profiles')?.select('*')?.eq('id', user?.id)?.single(),
        supabase?.from('symptom_entries')?.select('*')?.eq('user_id', user?.id),
        supabase?.from('user_sessions')?.select('*')?.eq('user_id', user?.id),
        supabase?.from('privacy_settings')?.select('*')?.eq('user_id', user?.id)?.single()
      ]);

      const exportData = {
        profile: profile?.data,
        symptoms: symptoms?.data,
        sessions: sessions?.data,
        privacy: privacy?.data,
        exportedAt: new Date()?.toISOString()
      };

      logger?.audit('DATA_EXPORTED', user?.id);
      return { data: exportData, error: null };
    } catch (error) {
      logger?.error('Data export error', error, { requestId });
      return { data: null, error: { message: 'Failed to export data' } };
    }
  }
};

export default privacyService;